import { createContext, useContext, useState, useMemo, useCallback } from 'react';

const DashboardContext = createContext();

/**
 * 대시보드 컨텍스트 프로바이더
 * 채팅과 대시보드 간의 연동을 위한 액션들을 관리합니다.
 */
export const DashboardProvider = ({ children }) => {
  const [dashboardActions, setDashboardActions] = useState(null);
  const [editModeActions, setEditModeActions] = useState(null);
  const [dashboardTemplate, setDashboardTemplate] = useState(null);
  const [dashboardListActions, setDashboardListActions] = useState(null);
  const [dashboardList, setDashboardList] = useState([]); // 대시보드 목록 상태 추가
  const [isDashboardListLoading, setIsDashboardListLoading] = useState(true); // 초기 로딩 상태 추가
  
  /**
   * 대시보드 액션들을 등록합니다.
   * @param {Object} actions - 대시보드 액션 객체
   */
  const registerDashboardActions = useCallback((actions) => {
    setDashboardActions(actions);
  }, []);
  
  /**
   * 편집 모드 액션들을 등록합니다.
   * @param {Object} actions - 편집 모드 액션 객체
   */
  const registerEditModeActions = useCallback((actions) => {
    setEditModeActions(actions);
  }, []);

  /**
   * 대시보드 목록 액션들을 등록합니다.
   * @param {Object} actions - 대시보드 목록 액션 객체 (refreshDashboardList 함수 포함)
   */
  const registerDashboardListActions = useCallback((actions) => {
    setDashboardListActions(actions);
  }, []);
  
  /**
   * 대시보드 템플릿을 업데이트합니다.
   * @param {Object} template - 대시보드 템플릿 객체
   */
  const updateDashboardTemplate = useCallback((template) => {
    setDashboardTemplate(template);
  }, []);

  /**
   * 대시보드 목록을 업데이트합니다.
   * @param {Array} dashboards - 대시보드 목록 배열
   */
  const updateDashboardList = useCallback((dashboards) => {
    setDashboardList(dashboards || []);
    setIsDashboardListLoading(false); // 목록이 업데이트되면 로딩 완료
  }, []);
  
  /**
   * 대시보드 액션들을 해제합니다.
   */
  const unregisterDashboardActions = useCallback(() => {
    setDashboardActions(null);
  }, []);
  
  /**
   * 편집 모드 액션들을 해제합니다.
   */
  const unregisterEditModeActions = useCallback(() => {
    setEditModeActions(null);
  }, []);

  /**
   * 대시보드 목록 액션들을 해제합니다.
   */
  const unregisterDashboardListActions = useCallback(() => {
    setDashboardListActions(null);
  }, []);

  /**
   * 대시보드 목록을 새로고침합니다.
   */
  const refreshDashboardList = async () => {
    if (dashboardListActions?.refreshDashboardList) {
      return await dashboardListActions.refreshDashboardList();
    } else {
      console.warn('[DashboardContext] 대시보드 목록 새로고침 함수가 등록되지 않음');
    }
  };

  /**
   * 현재 선택된 대시보드 ID를 업데이트합니다.
   */
  const setCurrentDashboardId = (dashboardId) => {
    if (dashboardListActions?.setCurrentDashboardId) {
      dashboardListActions.setCurrentDashboardId(dashboardId);
    } else {
      console.warn('[DashboardContext] 현재 대시보드 ID 업데이트 함수가 등록되지 않음');
    }
  };

  /**
   * 대시보드 템플릿을 해제합니다.
   */
  const unregisterDashboardTemplate = useCallback(() => {
    setDashboardTemplate(null);
  }, []);
  
  const value = useMemo(() => ({
    dashboardActions,
    editModeActions,
    dashboardTemplate,
    hasTemplate: !!dashboardTemplate,
    dashboardListActions,
    dashboardList,
    isDashboardListLoading, // 로딩 상태 추가
    registerDashboardActions,
    registerEditModeActions,
    registerDashboardListActions,
    updateDashboardTemplate,
    updateDashboardList,
    unregisterDashboardActions,
    unregisterEditModeActions,
    unregisterDashboardListActions,
    unregisterDashboardTemplate,
    refreshDashboardList,
    setCurrentDashboardId,
    setIsDashboardListLoading // 로딩 상태 설정 함수 추가
  }), [dashboardActions, editModeActions, dashboardTemplate, dashboardListActions, dashboardList, isDashboardListLoading]);

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};

/**
 * 대시보드 컨텍스트를 사용하기 위한 커스텀 훅
 */
export const useDashboardContext = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboardContext must be used within a DashboardProvider');
  }
  return context;
}; 